import { Box, Group, Stack, Text } from '@mantine/core';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef, useState } from 'react';

import {
  LINK_LIFETIME_MS,
  QR_CARD_PADDING,
  QR_SIZE,
  RING_CIRCUMFERENCE,
  RING_RADIUS,
  RING_SIZE,
} from './constants';

const TICK_INTERVAL_MS = 100;

export function ScanState({ link, updatedAt }: { link: string; updatedAt: number }) {
  return (
    <Stack align="center" gap="md">
      <motion.div
        key={link}
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        <Box bg="white" p={QR_CARD_PADDING} style={{ borderRadius: 12, lineHeight: 0 }}>
          <QRCodeSVG value={link} size={QR_SIZE} bgColor="#ffffff" fgColor="#000000" level="M" />
        </Box>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, delay: 0.08, ease: 'easeOut' }}
      >
        <Countdown updatedAt={updatedAt} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, delay: 0.16, ease: 'easeOut' }}
      >
        <ScanInstructions />
      </motion.div>
    </Stack>
  );
}

export function ScanInstructions() {
  return (
    <Text size="sm" c="dimmed" ta="center">
      In Telegram open Settings → Devices → Link Desktop Device.
    </Text>
  );
}

/**
 * Owns the countdown tick so ten re-renders a second stay inside the ring and its
 * label — re-rendering `QRCodeSVG` (hundreds of nodes) at that rate is wasteful.
 */
function Countdown({ updatedAt }: { updatedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  const clockOffset = useRef(Date.now() - updatedAt);

  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  const elapsed = Math.max(0, now - clockOffset.current - updatedAt);
  const progress = Math.min(1, elapsed / LINK_LIFETIME_MS);
  const secondsLeft = Math.max(0, Math.ceil((LINK_LIFETIME_MS - elapsed) / 1000));

  return (
    <Group gap="xs" align="center">
      <CountdownRing progress={progress} isExpiringSoon={secondsLeft <= 5} />
      <Text size="sm" c="dimmed">
        Refreshes in {secondsLeft}s
      </Text>
    </Group>
  );
}

function CountdownRing({
  progress,
  isExpiringSoon,
}: {
  progress: number;
  isExpiringSoon: boolean;
}) {
  return (
    <motion.svg
      width={RING_SIZE}
      height={RING_SIZE}
      viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      animate={isExpiringSoon ? { scale: [1, 1.12, 1] } : { scale: 1 }}
      transition={isExpiringSoon ? { duration: 1, repeat: Infinity, ease: 'easeInOut' } : undefined}
    >
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        fill="none"
        stroke="var(--mantine-color-dark-4)"
        strokeWidth={3}
      />
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        fill="none"
        stroke={isExpiringSoon ? 'var(--mantine-color-orange-5)' : 'var(--mantine-color-blue-5)'}
        strokeWidth={3}
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={RING_CIRCUMFERENCE * progress}
        transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
        style={{ transition: 'stroke 0.4s ease' }}
      />
    </motion.svg>
  );
}
